"use client";

import { toast } from "@dafiti/closet-ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryKey } from "@tanstack/react-query";
import { useLocale, useTranslations } from "next-intl";
import { useEnvContext } from "next-runtime-env";

import {
  getWishlistHeaders,
  wishlistPageQueryKey,
} from "../api/wishlist.query";
import type { WishlistProductsPageResponse } from "../types/wishlist.type";

type MutationContext = {
  previousData: [QueryKey, WishlistProductsPageResponse | undefined][];
};

export const useRemoveFromWishlist = () => {
  const t = useTranslations("wishlist");
  const locale = useLocale();
  const queryClient = useQueryClient();
  const { NEXT_PUBLIC_BFF_API: apiOrigin = "" } = useEnvContext();

  const wishlistPageKey = wishlistPageQueryKey(locale);

  const handleUndo = async (sku: string) => {
    try {
      const response = await fetch(`${apiOrigin}/api/v1/wishlist/product`, {
        method: "POST",
        body: JSON.stringify({ configurable_sku: sku }),
        headers: {
          "Content-Type": "application/json",
          ...getWishlistHeaders(),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to re-add product to wishlist");
      }
    } catch {
      toast({
        message: t("toast.undoError"),
        type: "error",
        icon: true,
        position: "top-right",
      });
    } finally {
      queryClient.invalidateQueries({ queryKey: wishlistPageKey });
    }
  };

  return useMutation<
    void,
    Error,
    { sku: string; productName: string },
    MutationContext
  >({
    mutationFn: async ({ sku }) => {
      const response = await fetch(
        `${apiOrigin}/api/v1/wishlist/product/${sku}`,
        {
          method: "DELETE",
          headers: getWishlistHeaders(),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to remove wishlist product");
      }
    },

    onMutate: async ({ sku }) => {
      await queryClient.cancelQueries({ queryKey: wishlistPageKey });

      const previousData =
        queryClient.getQueriesData<WishlistProductsPageResponse>({
          queryKey: wishlistPageKey,
        });

      queryClient.setQueriesData<WishlistProductsPageResponse>(
        { queryKey: wishlistPageKey },
        (old) =>
          old
            ? {
                ...old,
                products: old.products.filter((p) => p.sku !== sku),
              }
            : old,
      );

      return { previousData };
    },

    onError: (_error, _variables, context) => {
      context?.previousData.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });

      toast({
        message: t("toast.removeError"),
        type: "error",
        icon: true,
        position: "top-right",
      });
    },

    onSuccess: (_data, variables) => {
      toast({
        message: t("toast.removeSuccess", {
          productName: variables.productName,
        }),
        type: "info",
        icon: true,
        button: {
          text: t("toast.undo"),
          onClick: () => handleUndo(variables.sku),
        },
        position: "top-right",
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: wishlistPageKey });
    },
  });
};
